import { DateRange } from "react-day-picker";

import dayjs from "dayjs";
import * as XLSX from "xlsx";

import { supabase } from "~/integrations/supabase/client";
import { chunkArray } from "~/utils/arrayUtils";
import { grossFromNet, resolveGross } from "~/utils/iva";

const EXPORT_BATCH_SIZE = 1000;

interface KanbanExportParams {
  status: string;
  columnTitle: string;
  dateRange?: DateRange;
  viewAllSellers: boolean;
  userRole: string | null;
  userId: string | null;
}

interface AllSalesNotesExportParams {
  dateRange?: DateRange;
  viewAllSellers: boolean;
  userRole: string | null;
  userId: string | null;
  excludeStatuses?: string[];
}

interface RawOrder {
  id: string;
  client_id: string;
  total_amount: number;
  price_with_iva: number | null;
  is_exento: boolean | null;
  paid: boolean;
  factura_number?: string;
  created_at: string;
  clients: { name: string; tax_id: string | null } | null;
}

interface RawItem {
  id: string;
  sales_request_id: string;
  product_id: string;
  quantity: number;
  unit_price: number | null;
  discount_percent: number | null;
}

// Per-line net = qty × unit price × (1 − discount%), rounded. Verified against the
// live DB to sum exactly back to sales_request.total_amount for multi-item orders.
const lineNet = (item: RawItem): number =>
  Math.round(
    item.quantity * (item.unit_price ?? 0) * (1 - (item.discount_percent ?? 0) / 100)
  );
const lineGross = (net: number, isExento?: boolean | null): number =>
  grossFromNet(net, isExento);

/**
 * Fetches ALL sales requests for a given status from the database (no pagination limit)
 * and exports them to an Excel file.
 */
export const exportKanbanColumnToExcel = async ({
  status,
  columnTitle,
  dateRange,
  viewAllSellers,
  userRole,
  userId,
}: KanbanExportParams): Promise<void> => {
  // 1. Fetch all orders for this status in batches
  const allOrders: RawOrder[] = [];
  let offset = 0;
  let hasMore = true;

  while (hasMore) {
    let query = supabase
      .from("sales_request")
      .select(
        `
        id,
        client_id,
        total_amount,
        price_with_iva,
        is_exento,
        paid,
        created_at,
        clients:client_id(name, tax_id)
      `
      )
      .is("deleted_at", null);

    // Handle "facturado" as a virtual status (in_transit without transportista)
    if (status === "facturado") {
      query = query.eq("status", "in_transit").is("assigned_transportista", null);
    } else {
      query = query.eq("status", status);
    }

    query = query
      .order("created_at", { ascending: false })
      .range(offset, offset + EXPORT_BATCH_SIZE - 1);

    // Apply role-based ownership filters only (not status filters)
    // We're already filtering by a specific status, so we only need to filter by ownership
    if (userRole === "seller" && userId && !viewAllSellers) {
      query = query.eq("created_by", userId);
    } else if (userRole === "transportista" && userId) {
      // For transportista exporting facturado column, skip the assigned filter
      // since facturado orders have no assigned transportista by definition
      if (status !== "facturado") {
        query = query.eq("assigned_transportista", userId);
      }
    }
    // Note: cobranza status filter removed - it was overriding the column status filter

    // Apply date range filter
    if (dateRange?.from) {
      query = query.gte(
        "created_at",
        dayjs(dateRange.from).startOf("day").toISOString()
      );
    }
    if (dateRange?.to) {
      query = query.lte(
        "created_at",
        dayjs(dateRange.to).endOf("day").toISOString()
      );
    }

    const { data, error } = await query;
    if (error) throw error;

    allOrders.push(...((data as unknown as RawOrder[]) || []));
    hasMore = (data?.length || 0) === EXPORT_BATCH_SIZE;
    offset += EXPORT_BATCH_SIZE;
  }

  if (allOrders.length === 0) return;

  // 2. Fetch factura numbers for all orders
  const orderIds = allOrders.map((o) => o.id);
  const facturaMap: Record<string, string> = {};

  for (const chunk of chunkArray(orderIds, 100)) {
    const { data: docs } = await supabase
      .from("sales_request_documents")
      .select("sales_request_id, bsale_number")
      .in("sales_request_id", chunk)
      .eq("document_type", "factura")
      .not("bsale_number", "is", null);

    docs?.forEach((doc) => {
      if (doc.bsale_number && !facturaMap[doc.sales_request_id]) {
        facturaMap[doc.sales_request_id] = doc.bsale_number;
      }
    });
  }

  // 3. Fetch sales request items
  const itemsByOrder: Record<string, RawItem[]> = {};
  for (const chunk of chunkArray(orderIds, 100)) {
    const { data: items } = await supabase
      .from("sales_request_items")
      .select(
        "id, sales_request_id, product_id, quantity, unit_price, discount_percent"
      )
      .in("sales_request_id", chunk)
      .is("deleted_at", null);

    items?.forEach((item) => {
      if (!itemsByOrder[item.sales_request_id]) {
        itemsByOrder[item.sales_request_id] = [];
      }
      itemsByOrder[item.sales_request_id].push(item as RawItem);
    });
  }

  // 4. Fetch product barcodes
  const allProductIds = new Set<string>();
  Object.values(itemsByOrder)
    .flat()
    .forEach((item) => allProductIds.add(item.product_id));

  const productBarcodes: Record<string, string> = {};
  if (allProductIds.size > 0) {
    for (const chunk of chunkArray(Array.from(allProductIds), 100)) {
      const { data: products } = await supabase
        .from("products")
        .select("id, barcode")
        .in("id", chunk);

      products?.forEach((p) => {
        productBarcodes[p.id] = p.barcode || "-";
      });
    }
  }

  // 5. Build Excel rows
  const headers = [
    "Cliente",
    "RUT",
    "Codigo Producto",
    "Cantidad",
    "Precio Unitario",
    "Desc. %",
    "Factura",
    "Estado de Pago",
    "Monto Neto",
    "Monto Bruto",
    "Fecha Creacion",
  ];

  const data: (string | number)[][] = [];

  allOrders.forEach((order) => {
    const clientName =
      (order.clients as { name: string } | null)?.name || "Cliente sin nombre";
    const taxId =
      (order.clients as { tax_id: string | null } | null)?.tax_id || "-";
    const facturaNumber = facturaMap[order.id] || "-";
    const paymentStatus = order.paid ? "Pagado" : "Pendiente de pago";
    const netAmount = order.total_amount;
    const grossAmount = resolveGross(order);
    const createdDate = dayjs(order.created_at).format("DD/MM/YYYY");

    const items = itemsByOrder[order.id] || [];

    if (items.length === 0) {
      data.push([
        clientName,
        taxId,
        "-",
        "-",
        "-",
        "-",
        facturaNumber,
        paymentStatus,
        netAmount,
        grossAmount,
        createdDate,
      ]);
    } else {
      items.forEach((item) => {
        const net = lineNet(item);
        data.push([
          clientName,
          taxId,
          productBarcodes[item.product_id] || "-",
          item.quantity,
          item.unit_price ?? 0,
          item.discount_percent ?? 0,
          facturaNumber,
          paymentStatus,
          net,
          lineGross(net, order.is_exento),
          createdDate,
        ]);
      });
    }
  });

  // 6. Generate Excel file
  const worksheetData = [headers, ...data];
  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

  worksheet["!cols"] = [
    { wch: 30 }, // Cliente
    { wch: 15 }, // RUT
    { wch: 18 }, // Codigo Producto
    { wch: 10 }, // Cantidad
    { wch: 15 }, // Precio Unitario
    { wch: 10 }, // Desc. %
    { wch: 15 }, // Factura
    { wch: 18 }, // Estado de Pago
    { wch: 15 }, // Monto Neto
    { wch: 15 }, // Monto Bruto
    { wch: 15 }, // Fecha Creacion
  ];

  XLSX.utils.book_append_sheet(workbook, worksheet, "Notas de Venta");

  const sanitizedTitle = columnTitle.replace(/\n/g, "_").replace(/\s+/g, "_");
  const filename = `notas_${sanitizedTitle}_${dayjs().format("YYYY-MM-DD")}.xlsx`;
  XLSX.writeFile(workbook, filename);
};

// Status label mapping
const getStatusLabel = (
  status: string,
  assignedTransportista?: string | null
): string => {
  // Handle virtual "facturado" status: in_transit without transportista
  if (status === "in_transit" && !assignedTransportista) {
    return "Facturado";
  }

  const statusLabels: Record<string, string> = {
    pending: "Autorización Cobranza",
    pending_pricing: "Autorización por Precio",
    pending_credit: "Crédito Insuficiente",
    accepted: "Aprobada",
    prepaid: "Anticipado Pdte Pago",
    payment_to_check: "Validación Pago",
    por_facturar: "Por Facturar",
    facturado: "Facturado",
    in_transit: "En Tránsito",
    delivered: "Entregado",
    rejected: "Rechazada",
    test: "Test",
  };
  return statusLabels[status] || status;
};

interface RawOrderWithStatus extends RawOrder {
  status: string;
  assigned_transportista: string | null;
}

// Order of statuses for sheet tabs (workflow order)
const STATUS_ORDER = [
  "pending_pricing",
  "pending",
  "prepaid",
  "payment_to_check",
  "accepted",
  "por_facturar",
  "in_transit",
  "delivered",
  "rejected",
  "test",
];

/**
 * Fetches ALL sales requests from the database (excluding specified statuses)
 * and exports them to an Excel file with separate sheets per status.
 */
export const exportAllSalesNotesToExcel = async ({
  dateRange,
  viewAllSellers,
  userRole,
  userId,
  excludeStatuses = ["delivered", "rejected", "test"],
}: AllSalesNotesExportParams): Promise<void> => {
  // 1. Fetch all orders NOT in excluded statuses (batch loop)
  const allOrders: RawOrderWithStatus[] = [];
  let offset = 0;
  let hasMore = true;

  while (hasMore) {
    let query = supabase
      .from("sales_request")
      .select(
        `
        id,
        client_id,
        status,
        assigned_transportista,
        total_amount,
        price_with_iva,
        is_exento,
        paid,
        created_at,
        clients:client_id(name, tax_id)
      `
      )
      .is("deleted_at", null)
      .order("created_at", { ascending: false })
      .range(offset, offset + EXPORT_BATCH_SIZE - 1);

    // Exclude specified statuses
    if (excludeStatuses.length > 0) {
      query = query.not("status", "in", `(${excludeStatuses.join(",")})`);
    }

    // Apply role-based filters (same as useFetchSalesRequests.ts)
    if (userRole === "seller" && userId && !viewAllSellers) {
      query = query.eq("created_by", userId);
    } else if (userRole === "cobranza" && !viewAllSellers) {
      query = query.or(
        "status.eq.pending_pricing,status.eq.pending,status.eq.prepaid,status.eq.payment_to_check,status.eq.accepted"
      );
    } else if (userRole === "transportista") {
      query = query
        .eq("assigned_transportista", userId)
        .in("status", ["in_transit", "delivered", "rejected"]);
    }

    // Apply date range filter
    if (dateRange?.from) {
      query = query.gte(
        "created_at",
        dayjs(dateRange.from).startOf("day").toISOString()
      );
    }
    if (dateRange?.to) {
      query = query.lte(
        "created_at",
        dayjs(dateRange.to).endOf("day").toISOString()
      );
    }

    const { data, error } = await query;
    if (error) throw error;

    allOrders.push(...((data as unknown as RawOrderWithStatus[]) || []));
    hasMore = (data?.length || 0) === EXPORT_BATCH_SIZE;
    offset += EXPORT_BATCH_SIZE;
  }

  if (allOrders.length === 0) return;

  // 2. Fetch factura numbers for all orders
  const orderIds = allOrders.map((o) => o.id);
  const facturaMap: Record<string, string> = {};

  for (const chunk of chunkArray(orderIds, 100)) {
    const { data: docs } = await supabase
      .from("sales_request_documents")
      .select("sales_request_id, bsale_number")
      .in("sales_request_id", chunk)
      .eq("document_type", "factura")
      .not("bsale_number", "is", null);

    docs?.forEach((doc) => {
      if (doc.bsale_number && !facturaMap[doc.sales_request_id]) {
        facturaMap[doc.sales_request_id] = doc.bsale_number;
      }
    });
  }

  // 3. Fetch sales request items
  const itemsByOrder: Record<string, RawItem[]> = {};
  for (const chunk of chunkArray(orderIds, 100)) {
    const { data: items } = await supabase
      .from("sales_request_items")
      .select(
        "id, sales_request_id, product_id, quantity, unit_price, discount_percent"
      )
      .in("sales_request_id", chunk)
      .is("deleted_at", null);

    items?.forEach((item) => {
      if (!itemsByOrder[item.sales_request_id]) {
        itemsByOrder[item.sales_request_id] = [];
      }
      itemsByOrder[item.sales_request_id].push(item as RawItem);
    });
  }

  // 4. Fetch product barcodes and names
  const allProductIds = new Set<string>();
  Object.values(itemsByOrder)
    .flat()
    .forEach((item) => allProductIds.add(item.product_id));

  const productBarcodes: Record<string, string> = {};
  const productNames: Record<string, string> = {};
  if (allProductIds.size > 0) {
    for (const chunk of chunkArray(Array.from(allProductIds), 100)) {
      const { data: products } = await supabase
        .from("products")
        .select("id, barcode, short_name")
        .in("id", chunk);

      products?.forEach((p) => {
        productBarcodes[p.id] = p.barcode || "-";
        productNames[p.id] = p.short_name || "-";
      });
    }
  }

  // 5. Sort orders by status (workflow order)
  const statusOrderMap = STATUS_ORDER.reduce(
    (acc, status, index) => {
      acc[status] = index;
      return acc;
    },
    {} as Record<string, number>
  );

  allOrders.sort((a, b) => {
    const orderA = statusOrderMap[a.status] ?? 999;
    const orderB = statusOrderMap[b.status] ?? 999;
    return orderA - orderB;
  });

  // 6. Build Excel with single sheet, rows organized by status
  const headers = [
    "Estado",
    "Cliente",
    "RUT",
    "Codigo Producto",
    "Nombre Producto",
    "Cantidad",
    "Precio Unitario",
    "Desc. %",
    "Factura",
    "Estado de Pago",
    "Monto Neto",
    "Monto Bruto",
    "Fecha Creacion",
  ];

  const colWidths = [
    { wch: 25 }, // Estado
    { wch: 30 }, // Cliente
    { wch: 15 }, // RUT
    { wch: 18 }, // Codigo Producto
    { wch: 30 }, // Nombre Producto
    { wch: 10 }, // Cantidad
    { wch: 15 }, // Precio Unitario
    { wch: 10 }, // Desc. %
    { wch: 15 }, // Factura
    { wch: 18 }, // Estado de Pago
    { wch: 15 }, // Monto Neto
    { wch: 15 }, // Monto Bruto
    { wch: 15 }, // Fecha Creacion
  ];

  const data: (string | number)[][] = [];

  allOrders.forEach((order) => {
    const statusLabel = getStatusLabel(order.status, order.assigned_transportista);
    const clientName =
      (order.clients as { name: string } | null)?.name || "Cliente sin nombre";
    const taxId =
      (order.clients as { tax_id: string | null } | null)?.tax_id || "-";
    const facturaNumber = facturaMap[order.id] || "-";
    const paymentStatus = order.paid ? "Pagado" : "Pendiente de pago";
    const netAmount = order.total_amount;
    const grossAmount = resolveGross(order);
    const createdDate = dayjs(order.created_at).format("DD/MM/YYYY");

    const items = itemsByOrder[order.id] || [];

    if (items.length === 0) {
      data.push([
        statusLabel,
        clientName,
        taxId,
        "-",
        "-",
        "-",
        "-",
        "-",
        facturaNumber,
        paymentStatus,
        netAmount,
        grossAmount,
        createdDate,
      ]);
    } else {
      items.forEach((item) => {
        const net = lineNet(item);
        data.push([
          statusLabel,
          clientName,
          taxId,
          productBarcodes[item.product_id] || "-",
          productNames[item.product_id] || "-",
          item.quantity,
          item.unit_price ?? 0,
          item.discount_percent ?? 0,
          facturaNumber,
          paymentStatus,
          net,
          lineGross(net, order.is_exento),
          createdDate,
        ]);
      });
    }
  });

  const workbook = XLSX.utils.book_new();
  const worksheetData = [headers, ...data];
  const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
  worksheet["!cols"] = colWidths;
  XLSX.utils.book_append_sheet(workbook, worksheet, "Notas de Venta");

  // 7. Generate Excel file
  const filename = `notas_todas_${dayjs().format("YYYY-MM-DD")}.xlsx`;
  XLSX.writeFile(workbook, filename);
};
